import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock, TableBlock } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { holdsPermission } from '@/lib/auth/session'

import type { Caller } from './handles'
import { callerBeside } from './writes'
import {
  archiveForm, newScheduleForm, nextVersionForm, readPriceListDraft, reinstateForm,
  type PriceListDraft, type ScheduleStanding,
} from './pricingcontrols'

/**
 * Pricing & Billing › Price lists (`pr-lists`), read from
 * `GET /v1/admin/price-lists`, with one schedule's version opening from a row
 * via `GET /v1/admin/price-lists/{code}/versions/{version}`
 * (handler `internal/server/admin_pricelist.go`).
 *
 * # Editing does not exist here, and the screen has to say so
 *
 * Law 6: a versioned entity edits by creating a new version. The backend is
 * blunter — "there is no field here that can be changed: editing is publishing
 * the next version, which is what makes a fee charged in January explainable in
 * July". So there is no edit control, no Save and no draft field anywhere here.
 *
 * What there IS, since #152, is publishing (`pricingcontrols.ts`): a new
 * schedule from the catalogue, and the next version of an existing one from a
 * version's own page — where the lines in force can be read back and diffed
 * against. Both are labelled "Save as new version" because that is what the
 * call does, and creating a schedule and repricing one are the same route on
 * purpose.
 *
 * # `live` and `governs_now` are different questions
 *
 * `live` means `effective_to` is null — the version that is OPEN. `governs_now`
 * means in force at this moment. A version scheduled to start next month is
 * open and governs nothing, and a console that rendered "live" as "in force"
 * would show an operator a fee schedule nobody is being charged under. The
 * schema says which to read for "in force", and this reads that one.
 *
 * # A null `live_version` is a fact, not a zero
 *
 * A code whose only version starts next month has no version governing today.
 * Rendering the null as `0` would offer a version to link to that does not
 * exist — and would read as "version zero", which sounds like a beginning
 * rather than an absence.
 *
 * # `assignments` does not count the customers on the default
 *
 * They hold no assignment row, which is exactly why the default schedule cannot
 * be archived. A console that read the count as "how many customers are on this"
 * would report the default schedule as unused, on the one code that can never be
 * withdrawn.
 *
 * # Every amount carries its currency, and none is a float
 *
 * Laws 8 and 9. Fee lines are minor-unit integers beside an explicit ISO-4217
 * code — "never a decimal string and never a float, because the arithmetic
 * downstream is int64 throughout and a float in the wire format is where one
 * creeps back in". `percent_bps` is basis points, and it is rendered as basis
 * points rather than converted to a percentage with a decimal point.
 */

export const PRICE_LISTS_PATH = '/price-lists'

export function priceListVersionPath(code: string, version: number): string {
  return `${PRICE_LISTS_PATH}/${encodeURIComponent(code)}/versions/${String(version)}`
}

const SOURCE = 'postgres · price_lists + price_list_versions (GET /v1/admin/price-lists)'
const VERSION_SOURCE = 'postgres · price_list_items (GET /v1/admin/price-lists/{code}/versions/{version})'
const AUTHORITY = 'operational · what the fee path resolves against'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

interface Summary {
  readonly code: string
  readonly displayName: string
  readonly versions: number | null
  /** NULL is a real state: no version governs the queried instant. */
  readonly liveVersion: number | null
  readonly isDefault: boolean | null
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly assignable: boolean | null
  readonly archived: boolean
  readonly archivedReason: string
  readonly assignments: number | null
}

function readSummary(raw: unknown): Summary | null {
  if (!isObject(raw)) return null
  const code = text(raw['code'])
  if (code === '') return null
  const archived = isObject(raw['archived']) ? raw['archived'] : null
  return {
    code,
    displayName: text(raw['display_name']),
    versions: whole(raw['versions']),
    liveVersion: whole(raw['live_version']),
    isDefault: bool(raw['is_default']),
    effectiveFrom: text(raw['effective_from']),
    effectiveTo: text(raw['effective_to']),
    assignable: bool(raw['assignable']),
    archived: archived !== null,
    archivedReason: archived === null ? '' : text(archived['reason']),
    assignments: whole(raw['assignments']),
  }
}

function day(at: string): string {
  return at === '' ? '' : at.slice(0, 10)
}

/**
 * The version cell.
 *
 * A null live version says nothing governs today, in words. `v0` would be a
 * version to link to that does not exist, and reads as a beginning rather than
 * an absence.
 */
function versionCell(s: Summary): ChipCell {
  if (s.liveVersion === null) {
    return [s.versions === null || s.versions === 0 ? 'no version published' : 'none governs today', 'a']
  }
  return [`v${String(s.liveVersion)}`, 'g']
}

function assignableCell(s: Summary): ChipCell {
  if (s.archived) {
    return [s.archivedReason === '' ? 'WITHDRAWN' : `WITHDRAWN — ${s.archivedReason}`, 'r']
  }
  if (s.assignable === null) return ['— not stated', 'r']
  return s.assignable ? ['on sale', 'g'] : ['not assignable', 'a']
}

const COLS: readonly Column[] = [
  ['Code', 'm', 'minmax(160px,1.1fr)'],
  ['Name', 'x', 'minmax(170px,1.2fr)'],
  ['Governs today', 'c', '170px'],
  ['Versions', 'n', '96px'],
  ['Effective', 'm', '190px'],
  ['Assignments', 'n', '120px'],
  ['On sale', 'c', 'minmax(160px,1.2fr)'],
]

function row(s: Summary): readonly Cell[] {
  return [
    s.code,
    s.displayName === '' ? '— no name' : s.displayName,
    versionCell(s),
    s.versions === null ? '—' : String(s.versions),
    s.effectiveFrom === '' && s.effectiveTo === ''
      ? '— not stated'
      : `${day(s.effectiveFrom) === '' ? '—' : day(s.effectiveFrom)} → ${day(s.effectiveTo) === '' ? 'open' : day(s.effectiveTo)}`,
    // Does NOT include customers on the default, who hold no assignment row.
    s.assignments === null ? '—' : String(s.assignments),
    assignableCell(s),
  ]
}

function catalogueTable(rows: readonly Summary[], unreadable: number): TableBlock {
  const ungoverned = rows.filter((s) => s.liveVersion === null).length
  const withdrawn = rows.filter((s) => s.archived).length
  const head = `${String(rows.length)} schedule code(s)`
  return {
    t: 'table',
    title: 'price_lists · by code',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${head}${ungoverned === 0 ? '' : ` · ${String(ungoverned)} with no version governing today`}${withdrawn === 0 ? '' : ` · ${String(withdrawn)} withdrawn`}${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'The assignment count does NOT include customers who reach a code as the default — they hold no assignment row, which is exactly why the default schedule cannot be withdrawn. A code with no version governing today is a real state, not a version zero.',
  }
}

const NO_EDIT: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Law 6',
  title: 'There is no edit control here, and there is no field to edit',
  body: 'A fee schedule version has nothing that can be changed: editing IS publishing the next version, which is what makes a fee charged in January explainable in July. The version number is never chosen by a caller — the store allocates it and closes the incumbent at the new boundary — and the publisher is the authenticated operator rather than a field somebody fills in.',
  ref: 'publishing is the control below · a raise cannot skip the PSD2 notice period',
}

/**
 * Assigning is still not here, and this is why.
 *
 * The two assignment routes exist. Nothing lists assignments — there is no
 * `GET /price-list-assignments` and no source method that would answer one — so
 * a control here would create a row the operator could never see again. That is
 * the disposal law read from the other end (go-wallet#862, filed as #877), and
 * the honest rendering of it is this note rather than a button.
 */
const NO_ASSIGN: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'Not built',
  title: 'Moving a customer onto a schedule is not offered here, because nothing can show you the result',
  body: 'Both assignment routes exist — one to assign, one to close an assignment. What does not exist is any read that lists assignments, so an operator who moved a customer or a cohort onto a schedule would have no way to see what is assigned, or to find the id needed to undo it. A control whose effect cannot be observed is a control that cannot be checked, and this console will not draw one. The one place an assignment is visible today is a single customer’s price-list preview, one customer at a time.',
  ref: 'go-wallet#877 · a read that lists assignments · argued as a design-brief law on go-wallet#862',
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Price lists',
    title: 'No schedule code came back at all',
    body: 'Exactly one code is the live default, and every customer with no assignment resolves to it — so a catalogue with nothing in it is not "only the default exists", it is a read that found no default either. Every fee charged resolves against one of these rows.',
    ref: 'price_lists · the read returned no codes',
  }
}

function unreadableRowsNote(count: number): Block {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Partial read',
    title: `${String(count)} schedule code(s) could not be read`,
    body: 'Counted rather than dropped. A catalogue shorter than the record hides a schedule somebody is being charged under.',
    ref: 'a code with no key identifies no schedule and is refused',
  }
}

export function composePriceLists(
  payload: Record<string, unknown>, readAt: Date, caller: Caller, api: ApiClient,
): ScreenData {
  const raw = Array.isArray(payload['price_lists']) ? (payload['price_lists'] as readonly unknown[]) : []
  const rows: Summary[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readSummary(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [catalogueTable(rows, unreadable)]
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyNote())

  const defaultCheck = defaultAnomaly(rows)
  if (defaultCheck !== null) blocks.push(defaultCheck)


  blocks.push(NO_EDIT, NO_ASSIGN)
  blocks.push(newScheduleForm(api, caller))
  blocks.push(archiveForm(api, caller, standings(rows)))
  blocks.push(reinstateForm(api, caller, standings(rows)))

  return {
    blocks,
    provenance: {
      kind: 'live',
      source: SOURCE,
      authority: AUTHORITY,
      readAt: readAtStamp(readAt),
    },
    // The code a row opens into. A row is opened into its version's fee lines.
    rowKeys: rows.map((s) => (s.liveVersion === null ? null : `${s.code}@${String(s.liveVersion)}`)),
  }
}

// ---------------------------------------------------------------------------
// One version's fee lines
// ---------------------------------------------------------------------------

interface Placed {
  readonly ok: boolean
  readonly minor: number | null
  readonly reason: string
}

function place(value: unknown): Placed {
  const minor = exactMinorBig(value)
  if (minor === null) return { ok: false, minor: null, reason: 'not an exact integer' }
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < -BigInt(Number.MAX_SAFE_INTEGER)) {
    return { ok: false, minor: null, reason: 'beyond the exact integer range of an amount cell' }
  }
  return { ok: true, minor: Number(minor), reason: '' }
}

interface Item {
  readonly id: number | null
  readonly kind: string
  readonly method: string
  readonly currency: string
  readonly flat: Placed
  readonly percentBps: number | null
  readonly minFee: Placed | null
  readonly maxFee: Placed | null
  readonly labels: readonly (readonly [string, string])[]
}

function readItem(raw: unknown): Item | null {
  if (!isObject(raw)) return null
  const kind = text(raw['kind'])
  if (kind === '') return null
  const labels: (readonly [string, string])[] = []
  if (isObject(raw['labels'])) {
    for (const [lang, label] of Object.entries(raw['labels'])) {
      if (typeof label === 'string') labels.push([lang, label])
    }
  }
  return {
    id: whole(raw['id']),
    kind,
    method: text(raw['method']),
    currency: text(raw['currency']),
    flat: place(raw['flat_minor']),
    percentBps: whole(raw['percent_bps']),
    minFee: raw['min_fee_minor'] === undefined || raw['min_fee_minor'] === null ? null : place(raw['min_fee_minor']),
    maxFee: raw['max_fee_minor'] === undefined || raw['max_fee_minor'] === null ? null : place(raw['max_fee_minor']),
    labels,
  }
}

/**
 * A fee amount, with its currency or not at all.
 *
 * Law 8: no bare number. An item whose currency did not arrive has no amount
 * this console will draw — a fee figure without its unit is the one number on
 * this screen somebody might quote to a customer.
 */
function feeCell(p: Placed, currency: string): Cell {
  if (!isCurrencyCode(currency)) return '— no currency on this line'
  if (!p.ok) return `— ${p.reason}`
  return [p.minor ?? 0, currency]
}

const ITEM_COLS: readonly Column[] = [
  ['Kind', 'm', 'minmax(180px,1.3fr)'],
  ['Method', 'm', '140px'],
  ['Flat', 'a', '150px'],
  ['Rate', 'n', '120px'],
  ['Min fee', 'a', '150px'],
  ['Max fee', 'a', '150px'],
  ['Customer-facing label', 'x', 'minmax(190px,1.4fr)'],
]

function itemRow(i: Item): readonly Cell[] {
  return [
    i.kind,
    // A method-keyed kind without one would price every rail at once; a kind
    // that takes none renders as taking none rather than as missing something.
    i.method === '' ? '— not method-keyed' : i.method,
    feeCell(i.flat, i.currency),
    // Basis points, rendered as basis points. Converting to a percentage puts a
    // decimal point on a screen whose whole discipline is integers.
    i.percentBps === null ? '—' : `${String(i.percentBps)} bps`,
    i.minFee === null ? '— none' : feeCell(i.minFee, i.currency),
    i.maxFee === null ? '— none' : feeCell(i.maxFee, i.currency),
    i.labels.length === 0
      ? '— no label in any language'
      : i.labels.map(([lang, label]) => `${lang}: ${label}`).join(' · '),
  ]
}

export function composePriceListVersion(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const code = text(payload['code'])
  const version = whole(payload['version'])
  const live = bool(payload['live'])
  const governsNow = bool(payload['governs_now'])
  const isDefault = bool(payload['is_default'])
  const effectiveFrom = text(payload['effective_from'])
  const effectiveTo = text(payload['effective_to'])

  const raw = Array.isArray(payload['items']) ? (payload['items'] as readonly unknown[]) : []
  const items: Item[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readItem(entry)
    if (parsed === null) unreadable += 1
    else items.push(parsed)
  }

  const title = `${code === '' ? 'schedule' : code} v${version === null ? '?' : String(version)}`

  const blocks: Block[] = [{
    t: 'kv',
    title: `${title} — the version itself`,
    items: [
      [
        'in force now',
        governsNow === null ? '— not stated' : governsNow ? 'yes' : 'no',
        governsNow === true ? 'IN FORCE' : '',
        governsNow === null ? 'r' : governsNow ? 'g' : 'n',
        'Read from `governs_now`, not from `live`. A version whose effective_to is null is OPEN, which is a different fact — a version that starts next month is open and in force over nothing.',
        '',
      ],
      [
        'open-ended',
        live === null ? '— not stated' : live ? 'yes — no end date' : 'no — it has been closed',
        '',
        'n',
        'Closed means a later version took over at a boundary. Nothing was edited; the successor was published.',
        '',
      ],
      [
        'effective',
        `${day(effectiveFrom) === '' ? '—' : day(effectiveFrom)} → ${day(effectiveTo) === '' ? 'open' : day(effectiveTo)}`,
        '',
        'n',
        'The window this version governs. A fee charged inside it is explained by this row and no other.',
        'm',
      ],
      [
        'the default schedule',
        isDefault === null ? '— not stated' : isDefault ? 'yes' : 'no',
        '',
        'n',
        'The schedule for customers with no assignment. Exactly one code is the live default, and it cannot be withdrawn.',
        '',
      ],
    ],
  }]

  const table: TableBlock = {
    t: 'table',
    title: `${title} · priced lines`,
    src: 'ledger',
    facets: false,
    cols: ITEM_COLS,
    rows: items.map(itemRow),
    footer: `${String(items.length)} line(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'Every amount is a minor-unit integer beside its ISO-4217 currency — never a decimal string and never a float, because the arithmetic downstream is int64 throughout. A rate is basis points and is shown as basis points: converting to a percentage puts a decimal point on a screen whose whole discipline is integers.',
  }
  blocks.push(table)

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Partial read',
      title: `${String(unreadable)} priced line(s) could not be read`,
      body: 'Counted rather than dropped. A schedule rendered with a line missing prices something at nothing on the screen an operator checks a disputed fee against.',
      ref: 'a line with no kind prices nothing and is refused',
    })
  }

  if (items.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'No lines',
      title: 'This version prices nothing',
      body: 'A published version with no priced line charges nothing for everything it covers. That is a legitimate schedule — a free tier is one — and it is stated rather than left as an empty table somebody reads as a failed load.',
      ref: `${title} · items = []`,
    })
  }

  return {
    blocks,
    provenance: {
      kind: 'live',
      source: VERSION_SOURCE,
      authority: AUTHORITY,
      readAt: readAtStamp(readAt),
    },
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(reason: string, decisionRef: string | null, source: string): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Price lists',
      title: 'The fee schedules could not be read',
      body: `${reason} No code, no version and no priced line below is known. Every fee this wallet charges resolves against one of these rows, so an unread catalogue is not an empty one — and it is not evidence that nothing is being charged.`,
      ref: decisionRef === null ? 'No schedule was read.' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

export const loadPriceLists: ScreenLoader = async (api) => {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(PRICE_LISTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The price-list endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
        SOURCE,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null, SOURCE)
  }
  if (!isObject(payload)) {
    return unreadableScreen('The price-list endpoint returned a shape this console does not know.', null, SOURCE)
  }
  return composePriceLists(payload, new Date(), await calling, api)
}

/**
 * The catalogue rows as the controls read them.
 *
 * `assignable` is three-state on the read and two-state here, and the collapse
 * is deliberate in the safe direction: a row whose `assignable` did not arrive
 * is treated as NOT assignable, so it is offered for reinstatement rather than
 * for withdrawal. Offering to withdraw a schedule whose standing this console
 * could not establish is the mistake with consequences; offering to reinstate
 * one that was never withdrawn is refused by the backend and costs a round trip.
 */
function standings(rows: readonly Summary[]): readonly ScheduleStanding[] {
  return rows.map((r) => ({
    code: r.code,
    displayName: r.displayName === '' ? r.code : r.displayName,
    assignable: r.assignable === true,
    isDefault: r.isDefault === true,
  }))
}

/**
 * One schedule version, opened from a catalogue row.
 *
 * Hangs off a row rather than off a screen, the same shape `readDecision` and
 * `readAMLCase` have: `pr-items` is the design's vocabulary screen and is NOT
 * this — the fee-item vocabulary is a catalogue of kinds, and what this returns
 * is one schedule's priced lines. Registering this as `pr-items` would put one
 * schedule's prices under a heading that promises the vocabulary they are drawn
 * from.
 */

const HISTORY_COLS: readonly Column[] = [
  ['Version', 'm', '90px'],
  ['Effective', 'm', '190px'],
  ['In force', 'c', '150px'],
  ['Published by', 'x', '150px'],
]

const ARCHIVAL_COLS: readonly Column[] = [
  ['Withdrawn', 'm', '130px'],
  ['By', 'x', '150px'],
  ['Reason', 'x', 'minmax(200px,1.4fr)'],
  ['Reinstated', 'm', '130px'],
]

/**
 * A schedule's other versions, and every time it was withdrawn.
 *
 * A version history is a READ, not the editor's surface: it is the record of
 * what was true, which is exactly what makes a fee charged in January
 * explainable in July. The DRAFT endpoint is the editor's — a draft exists only
 * to be published, and rendering one without the control that publishes it
 * shows an operator something they cannot act on.
 *
 * Both reads fail independently of the version itself: an operator who came to
 * see what a schedule charges should still see it when its history does not
 * load.
 */
async function scheduleHistory(
  api: ApiClient,
  code: string,
  suffix: string,
  key: string,
): Promise<readonly unknown[] | null> {
  try {
    const payload = await api.get<unknown>(`${PRICE_LISTS_PATH}/${encodeURIComponent(code)}/${suffix}`)
    if (!isObject(payload)) return null
    const list = payload[key]
    return Array.isArray(list) ? (list as readonly unknown[]) : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
}

export async function readPriceListVersion(
  api: ApiClient,
  key: string,
): Promise<ScreenData> {
  const at = key.lastIndexOf('@')
  const code = at === -1 ? key : key.slice(0, at)
  const version = at === -1 ? null : whole(Number(key.slice(at + 1)))
  if (code === '' || version === null) {
    const reason = 'No schedule code and version was given, so nothing was looked up.'
    return unreadableScreen(reason, null, VERSION_SOURCE)
  }

  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(priceListVersionPath(code, version))
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The version endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
        VERSION_SOURCE,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null, VERSION_SOURCE)
  }
  if (!isObject(payload)) {
    return unreadableScreen('The version endpoint returned a shape this console does not know.', null, VERSION_SOURCE)
  }
  const data = composePriceListVersion(payload, new Date())
  const blocks = [...data.blocks]
  const missing: string[] = []

  const versions = await scheduleHistory(api, code, 'versions', 'versions')
  if (versions === null) missing.push(`the other versions of ${code}`)
  else {
    blocks.push({
      t: 'table',
      title: `${code} · every version`,
      src: 'ledger',
      facets: false,
      cols: HISTORY_COLS,
      rows: versions.filter(isObject).map((v) => [
        whole(v['version']) === null ? '—' : `v${String(whole(v['version']))}`,
        `${day(text(v['effective_from'])) === '' ? '—' : day(text(v['effective_from']))} → ${day(text(v['effective_to'])) === '' ? 'open' : day(text(v['effective_to']))}`,
        bool(v['governs_now']) === true
          ? (['in force now', 'g'] as ChipCell)
          : bool(v['live']) === true
            ? (['published, not yet in force', 'a'] as ChipCell)
            : (['superseded', 'n'] as ChipCell),
        text(v['published_by']) === '' ? '— unattributed' : text(v['published_by']),
      ] as readonly Cell[]),
      footer: `${String(versions.length)} version(s)`,
      foot2:
        'Superseded versions are kept because a fee charged under one is explained by it and by nothing else. Nothing was ever edited: each version closed the one before it at a boundary.',
    })
  }

  const archivals = await scheduleHistory(api, code, 'archivals', 'archivals')
  if (archivals === null) missing.push(`whether ${code} has ever been withdrawn`)
  else if (archivals.length > 0) {
    blocks.push({
      t: 'table',
      title: `${code} · withdrawals`,
      src: 'ledger',
      facets: false,
      cols: ARCHIVAL_COLS,
      rows: archivals.filter(isObject).map((a) => [
        day(text(a['archived_at'])) === '' ? '— not returned' : day(text(a['archived_at'])),
        text(a['archived_by']) === '' ? '— unattributed' : text(a['archived_by']),
        `${text(a['reason_code']) === '' ? '— no code' : text(a['reason_code'])}${text(a['reason']) === '' ? '' : ` · ${text(a['reason'])}`}`,
        day(text(a['reinstated_at'])) === '' ? 'still withdrawn' : day(text(a['reinstated_at'])),
      ] as readonly Cell[]),
      footer: `${String(archivals.length)} withdrawal(s)`,
      foot2:
        'A withdrawal is a period, not a flag: reinstated ones stay here because an assignment dated inside one was refused at the time, and that is what explains a gap somebody is asking about.',
    })
  }

  // The next version is published from HERE rather than from the catalogue,
  // because publishing one means starting from the lines in force — and this is
  // the only surface that knows which version those are. The catalogue's own
  // control creates a schedule that does not exist yet, where there is nothing
  // to start from.
  const caller = await calling
  const draft = await draftBeside(api, caller, code, version)
  blocks.push(nextVersionForm(api, caller, {
    code,
    displayName: text(payload['display_name']) === '' ? code : text(payload['display_name']),
    // The version read says nothing about whether the SCHEDULE is on sale —
    // that is a catalogue fact. Publishing does not depend on it (a withdrawn
    // schedule can still be repriced; what it cannot be is newly assigned), so
    // this is stated as true rather than guessed at from a field that is absent.
    assignable: true,
    isDefault: bool(payload['is_default']) === true,
  }, draft))

  return {
    ...data,
    blocks,
    provenance: missing.length === 0
      ? data.provenance
      : { kind: 'partial', source: VERSION_SOURCE, authority: AUTHORITY, readAt: readAtStamp(new Date()), missing },
  }
}

/**
 * The clone-to-draft read, attempted only when it could succeed.
 *
 * The draft endpoint carries the PUBLISH permission rather than the read one —
 * it is the authoring surface. So a support session reading a schedule would
 * get a 403 for asking, which is a refusal in the log and in somebody's network
 * panel that tells nobody anything: the control it feeds is already disabled
 * for that same session, by that same permission, with the role named.
 *
 * Asking anyway would also be the console second-guessing an authorization
 * decision, which it does not do. This is not that: it is declining to make a
 * request whose answer cannot change what is drawn.
 */
async function draftBeside(
  api: ApiClient, caller: Caller, code: string, version: number,
): Promise<PriceListDraft | null> {
  if (!holdsPermission(caller.session, 'price_list.publish')) return null
  return readPriceListDraft(api, code, version)
}

// ---------------------------------------------------------------------------
// What prices ONE customer — GET /v1/admin/customers/{customerID}/price-list
// ---------------------------------------------------------------------------

/**
 * The schedule that prices one customer, and the rung that decided.
 *
 * Answered by the SAME function the fee path calls, "so it cannot disagree with
 * what the customer is actually charged" — which is what makes this worth
 * having at all. A console that resolved the schedule itself would be a second
 * implementation of the pricing rung, and the two would disagree about somebody
 * eventually.
 *
 * # The rung is the answer, not a footnote
 *
 * `scope` says which rung won, and "'somebody put them on it' and 'it is the
 * default' are different answers". A customer on the default holds no
 * assignment row by design — `assignment_id` is absent there rather than zero —
 * and an operator asked "why is this customer on this price" needs to know
 * which of the two they are looking at before they go looking for a row.
 *
 * # On the segment rung, the evidence is which cohort won
 *
 * `segment` and `precedence` are present there, and `precedence` is what makes
 * "which of the three cohorts this customer is in decided" answerable at all —
 * lower is stronger, and it is unique among overlapping segment assignments
 * precisely so the question has exactly one answer.
 *
 * Hangs off a row rather than a screen. See `openers.ts`.
 */
export function customerPriceListPath(customerID: string): string {
  return `/customers/${encodeURIComponent(customerID)}/price-list`
}

const RESOLUTION_SOURCE = 'postgres · the fee path’s own resolver (GET /v1/admin/customers/{id}/price-list)'

export async function readCustomerPriceList(api: ApiClient, customerID: string): Promise<ScreenData> {
  const wanted = customerID.trim()
  if (wanted === '') {
    const reason = 'No customer was given, so no schedule was resolved.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'Pricing', title: 'No customer was requested', body: reason, ref: 'a row with no id identifies nobody' }],
      provenance: { kind: 'unread', source: RESOLUTION_SOURCE, reason },
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(customerPriceListPath(wanted))
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    const reason = error instanceof ApiError
      ? `The resolver answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
      : 'The backend could not be reached.'
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Pricing',
        title: `What prices customer ${wanted} could not be read`,
        body: `${reason} Which schedule governs them, and which rung decided, are both unknown — and this is the same resolver the fee path calls, so an unread answer here is not evidence that nothing is being charged.`,
        ref: error instanceof ApiError && error.decisionRef !== null ? `decision_ref ${error.decisionRef}` : `customer asked for: ${wanted}`,
      }],
      provenance: { kind: 'unread', source: RESOLUTION_SOURCE, reason },
    }
  }

  if (!isObject(payload)) {
    const reason = 'The resolver returned a shape this console does not know.'
    return {
      blocks: [{ t: 'note', tone: 'r', kicker: 'Pricing', title: `What prices customer ${wanted} could not be read`, body: reason, ref: `customer asked for: ${wanted}` }],
      provenance: { kind: 'unread', source: RESOLUTION_SOURCE, reason },
    }
  }

  const scope = text(payload['scope'])
  const listCode = text(payload['list_code'])
  const version = whole(payload['version'])
  const assignmentId = whole(payload['assignment_id'])
  const segment = text(payload['segment'])
  const precedence = whole(payload['precedence'])
  const reasonCode = text(payload['reason_code'])

  const blocks: Block[] = [{
    t: 'kv',
    title: `What prices customer ${wanted}`,
    items: [
      [
        'schedule',
        listCode === '' ? '— not stated' : `${listCode}${version === null ? '' : ` v${String(version)}`}`,
        '',
        listCode === '' ? 'r' : 'n',
        'Resolved by the same function the fee path calls, so it cannot disagree with what this customer is actually charged.',
        'm',
      ],
      [
        'which rung decided',
        scope === 'default'
          ? 'the default — nobody put them on it'
          : scope === 'customer'
            ? 'an assignment naming this customer'
            : scope === 'segment'
              ? `a cohort they are in${segment === '' ? '' : `: ${segment}`}`
              : scope === '' ? '— not stated' : `unknown rung: ${scope}`,
        scope === 'default' ? 'default' : scope === '' ? 'unread' : scope,
        scope === '' ? 'r' : scope === 'default' ? 'n' : 'a',
        '“Somebody put them on it” and “it is the default” are different answers, and an operator asked why this customer is on this price needs to know which before they go looking for a row.',
        '',
      ],
      [
        'the deciding row',
        // ABSENT on the default rung, where there is no row by design — and a
        // zero would send somebody looking for assignment zero.
        scope === 'default'
          ? 'none — a customer on the default holds no assignment row'
          : assignmentId === null ? '— not stated' : String(assignmentId),
        '',
        'n',
        'Which is why the default schedule cannot be archived: there is nothing to close.',
        'm',
      ],
      [
        'precedence',
        scope === 'segment'
          ? precedence === null ? '— not stated' : String(precedence)
          : 'not applicable off the segment rung',
        '',
        'n',
        'Lower is stronger, and it is unique among overlapping segment assignments precisely so “which cohort wins” has exactly one answer. A customer can be in three at once.',
        '',
      ],
      [
        'reason recorded',
        reasonCode === '' ? '— none' : reasonCode,
        '',
        'n',
        'The deciding assignment’s reason code — or `default_price_list` on the default rung, where nobody decided anything.',
        'm',
      ],
    ],
  }]

  // The version's own priced lines, from the same payload, so the answer to
  // "what are they charged" is on the same screen as "which schedule".
  if (isObject(payload['list'])) {
    const lines = composePriceListVersion(payload['list'], new Date())
    blocks.push(...lines.blocks)
  }

  return {
    blocks,
    provenance: { kind: 'live', source: RESOLUTION_SOURCE, authority: AUTHORITY, readAt: readAtStamp(new Date()) },
  }
}

/**
 * A catalogue that does not have exactly one default, stated as a finding.
 *
 * # Why this outlived the panel it came from
 *
 * It was one row of a `kv` block titled "The catalogue, as read", beside two
 * rows that only restated what the table already showed — how many codes have
 * no version governing today, and how many are withdrawn. Both are countable
 * from the rows on screen; this is not, and it is the one that matters.
 *
 * Exactly one code is the live default, and every customer with no assignment
 * resolves to it. None, or more than one, is a state the fee path cannot
 * resolve — so it is a finding rather than a figure, and it renders only when
 * it is true. A green "one" chip reported on every healthy read is the kind of
 * reassurance that teaches an operator to stop reading the panel.
 */
function defaultAnomaly(rows: readonly Summary[]): NoteBlock | null {
  const defaults = rows.filter((s) => s.isDefault === true)
  if (defaults.length === 1) return null
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Default schedule',
    title: defaults.length === 0
      ? 'No code reports itself as the default'
      : `${String(defaults.length)} codes claim to be the default`,
    body: 'Every customer with no assignment resolves to the default, so the fee path cannot resolve this.',
    ref: defaults.map((d) => d.code).join(' · '),
  }
}
